import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { 
    LinkIcon, 
    XMarkIcon, 
    UserPlusIcon,
    CreditCardIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import Helper from '../helpers';
import config from "../config";
import LayoutWrapper from './LayoutWrapper';

const baseUrl = config.apiBaseUrl;

const PaymentProviderLinks = () => {
    const [providers, setProviders] = useState({
        gocardless: false,
        stripe: false,
        paypal: false
    });
    const [loading, setLoading] = useState(true);
    const [currentVenueKey, setCurrentVenueKey] = useState("");
    const [currentVenueKeyOptions, setCurrentVenueKeyOptions] = useState([]);
    const navigate = useNavigate();

    const providerInfo = {
        gocardless: {
            name: 'GoCardless',
            description: 'Direct debit payments and recurring billing',
            logo: '/img/paymentProvider/gocardless.png',
            color: 'emerald'
        },
        // stripe: {
        //     name: 'Stripe',
        //     description: 'Credit card payments and online transactions',
        //     logo: '/img/paymentProvider/stripe.png',
        //     color: 'blue'
        // },
        // paypal: {
        //     name: 'PayPal',
        //     description: 'PayPal payments and digital wallet',
        //     logo: '/img/paymentProvider/paypal.png',
        //     color: 'yellow'
        // }
    };

    useEffect(() => {
        const fetchProvidersStatus = async () => {
            if(!currentVenueKey) return;
            try {
                const token = await Helper.getCookie('wh-jwt');
                const response = await fetch(`${baseUrl}/api/paymentadmin/admin/${currentVenueKey}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                const data = await response.json();
                const providersStatus = {
                    gocardless: data.some(provider => provider.name === 'GoCardless' && provider.enabled),
                    stripe: data.some(provider => provider.name === 'Stripe' && provider.enabled),
                    paypal: data.some(provider => provider.name === 'PayPal' && provider.enabled)
                };
                setProviders(providersStatus);
            } catch (error) {
                console.error('Error fetching provider status:', error);
            }
        };

        const getCurrentVenueKey = async () => {
            await Helper.getAdminVenues(setCurrentVenueKey, setCurrentVenueKeyOptions);
            setLoading(false);
        }

        getCurrentVenueKey().then(fetchProvidersStatus);
    }, [currentVenueKey]);

    const handleConnect = async (provider) => {
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/oauth/get-token-url/${currentVenueKey}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.authUrl) {
                window.location.href = data.authUrl;
            }
        } catch (error) {
            console.error('Error connecting provider:', error);
        }
    };

    const handleDisconnect = (provider) => {
        // Handle disconnect logic
        console.log(`Disconnecting ${provider}`);
    };

    const handleImportUsers = (provider) => {
        navigate(`/dashboard/import-users/${provider}`);
    };

    if (loading) {
        return (
            <LayoutWrapper title="Payment Integrations" subtitle="Manage your payment provider connections">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper title="Payment Integrations" subtitle="Connect and manage your payment providers">
            {/* Venue Selector (hidden but functional) */}
            {currentVenueKeyOptions.length > 0 && (
                <select
                    className="hidden"
                    value={currentVenueKey}
                    onChange={(e) => setCurrentVenueKey(e.target.value)}
                >
                    {currentVenueKeyOptions.map((venueKey) => (
                        <option key={venueKey} value={venueKey}>
                            {venueKey}
                        </option>
                    ))}
                </select>
            )}

            {/* Provider Cards */}
            <div className="grid gap-6">
                {Object.entries(providerInfo).map(([providerKey, info]) => {
                    const isConnected = providers[providerKey];
                    
                    return (
                        <div key={providerKey} className="backdrop-blur-sm bg-gray-900/20 border border-gray-700/30 rounded-xl p-6 hover:bg-gray-900/30 transition-all duration-300">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                                {/* Provider Info */}
                                <div className="flex items-center space-x-4">
                                    {/* Logo */}
                                    <div className="flex-shrink-0 w-16 h-16 bg-gray-800/50 rounded-xl p-3 flex items-center justify-center">
                                        <img
                                            src={info.logo}
                                            alt={`${info.name} logo`}
                                            className="w-full h-full object-contain"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                        <div className="hidden w-full h-full items-center justify-center">
                                            <CreditCardIcon className="h-8 w-8 text-gray-400" />
                                        </div>
                                    </div>

                                    {/* Provider Details */}
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <h3 className="text-lg font-semibold text-white">{info.name}</h3>
                                            {isConnected ? (
                                                <div className="flex items-center space-x-1 px-2 py-1 bg-emerald-600/20 border border-emerald-500/30 rounded-full">
                                                    <CheckCircleIcon className="h-4 w-4 text-emerald-400" />
                                                    <span className="text-xs font-medium text-emerald-400">Connected</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center space-x-1 px-2 py-1 bg-gray-600/20 border border-gray-500/30 rounded-full">
                                                    <ExclamationTriangleIcon className="h-4 w-4 text-gray-400" />
                                                    <span className="text-xs font-medium text-gray-400">Not Connected</span>
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-400">{info.description}</p>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                                    {/* Connect/Disconnect Button */}
                                    {isConnected ? (
                                        <button
                                            onClick={() => handleDisconnect(providerKey)}
                                            className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500/50"
                                        >
                                            <XMarkIcon className="h-4 w-4" />
                                            <span>Disconnect</span>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleConnect(providerKey)}
                                            className="flex items-center justify-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                                        >
                                            <LinkIcon className="h-4 w-4" />
                                            <span>Connect</span>
                                        </button>
                                    )}

                                    {/* Import Users Button */}
                                    <button
                                        onClick={() => handleImportUsers(providerKey)}
                                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                    >
                                        <UserPlusIcon className="h-4 w-4" />
                                        <span>Import Users</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Info Section */}
            <div className="mt-8 backdrop-blur-sm bg-blue-900/20 border border-blue-800/30 rounded-xl p-6">
                <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-600/20 rounded-full flex items-center justify-center">
                            <ExclamationTriangleIcon className="h-5 w-5 text-blue-400" />
                        </div>
                    </div>
                    <div>
                        <h4 className="text-lg font-medium text-white mb-2">Integration Information</h4>
                        <ul className="text-sm text-gray-300 space-y-1">
                            <li>• Connect payment providers to enable online payments for your venue</li>
                            <li>• Import users from connected payment providers to sync customer data</li>
                            <li>• Ensure you have admin access to configure payment settings</li>
                            <li>• Contact support if you encounter any connection issues</li>
                        </ul>
                    </div>
                </div>
            </div>
        </LayoutWrapper>
    );
};


export default PaymentProviderLinks;
